import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

interface LanguageRouterProps {
  children: React.ReactNode;
}

/**
 * Component that syncs URL language prefix with LanguageContext
 * - /en/* routes set language to English
 * - /* routes (no prefix) set language to Thai
 * - /th/* routes redirect to /* (canonical Thai URLs)
 */
const LanguageRouter = ({ children }: LanguageRouterProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();

  // Browser language detection on first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem("language_preference_set");
    const path = location.pathname;
    
    // Only auto-detect on first visit and when on Thai (default) routes
    if (!hasVisited && !path.startsWith("/en")) {
      const browserLang = navigator.language || (navigator.languages && navigator.languages[0]) || "th";
      const isThai = browserLang.toLowerCase().startsWith("th");
      
      if (!isThai) {
        // Redirect to English version
        const newPath = path === "/" ? "/en" : `/en${path}`;
        localStorage.setItem("language_preference_set", "true");
        navigate(newPath + location.search + location.hash, { replace: true });
        return;
      }
      
      // Mark as visited even for Thai speakers
      localStorage.setItem("language_preference_set", "true");
    }
  }, []);

  useEffect(() => {
    const path = location.pathname;
    
    // Handle /th/* redirects to canonical Thai URLs
    if (path.startsWith("/th")) {
      const newPath = path.replace(/^\/th/, "") || "/";
      navigate(newPath + location.search + location.hash, { replace: true });
      return;
    }
    
    // Sync language based on URL
    if (path.startsWith("/en")) {
      if (language !== "en") {
        setLanguage("en");
      }
    } else {
      if (language !== "th") {
        setLanguage("th");
      }
    }
  }, [location.pathname, setLanguage, navigate, language]);

  // Set html lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return <>{children}</>;
};

export default LanguageRouter;
